<template>
  <div class="activity-page">
    <div class="activity-container">
      <div class="activity-header">
        <div class="header-content">
          <h1 class="page-title">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
              <path d="M4 6a2 2 0 1 1 0 4 2 2 0 0 1 0-4m2.625.547a3 3 0 0 0-5.584.953H.5a.5.5 0 0 0 0 1h.541A3 3 0 0 0 1 8a3 3 0 0 0 .041.5H.5a.5.5 0 0 0 0 1h.541a3 3 0 0 0 5.584.953V10.5a.5.5 0 0 0 1 0V8.5a.5.5 0 0 0-1 0v-.5a.5.5 0 0 0-.041-.5h.791a.5.5 0 0 0 0-1h-.791A.5.5 0 0 0 6.625 6.547z"/>
              <path d="M11 8a2 2 0 1 1 0 4 2 2 0 0 1 0-4m2.625.547A3 3 0 0 0 11 6a3 3 0 0 0-2.625 2.547v-.5a.5.5 0 0 0-1 0v.5a.5.5 0 0 0 .041.5H6.625a.5.5 0 0 0 0 1h.791a.5.5 0 0 0 .041.5V10.5a.5.5 0 0 0 1 0v-.5a3 3 0 0 0 2.625-2.047V8.5a.5.5 0 0 0 1 0v-.953z"/>
            </svg>
            Monitoring d'activité
          </h1>
          <p class="page-subtitle">Surveillance et analyse de l'activité utilisateur</p>
        </div>
        <router-link :to="{ name: 'adminDashboard' }" class="back-button">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path fill-rule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"/>
          </svg>
          Retour au dashboard
        </router-link>
      </div>

      <div class="search-section">
        <div class="search-card">
          <h3 class="search-title">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
            </svg>
            Recherche par utilisateur ou domaine
          </h3>
          <div class="search-form">
            <div class="search-input-group">
              <input
                v-model="searchEmail"
                type="text"
                placeholder="Email (user@example.com) ou domaine (example.com)"
                class="search-input"
                @keyup.enter="searchUserOrDomain"
              />
              <button
                @click="searchUserOrDomain"
                :disabled="!searchEmail || searchingUser || searchingDomain"
                class="search-button"
              >
                <span v-if="!searchingUser && !searchingDomain">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                  </svg>
                  Rechercher
                </span>
                <span v-else class="loading-text">
                  <div class="spinner"></div>
                  Recherche...
                </span>
              </button>
            </div>
            <p class="search-hint">
              💡 <strong>Astuce :</strong> Entrez un domaine (ex: example.com) pour voir l'activité de tous les utilisateurs de ce domaine
            </p>
          </div>
        </div>
      </div>

      <!-- Domain Modal -->
      <div v-if="showDomainModal" class="modal-overlay" @click.self="showDomainModal = false">
        <div class="modal-container">
          <!-- Modal Header -->
          <div class="modal-header">
            <div>
              <h2 class="modal-title">Activité du domaine</h2>
              <p class="modal-subtitle">@{{ domainName }}</p>
            </div>
            <button @click="showDomainModal = false" class="modal-close">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Modal Content -->
          <div class="modal-content">
            <!-- Navigation mois -->
            <div class="month-nav">
              <button class="nav-arrow" @click="prevMonth" title="Mois précédent">‹</button>
              <span class="month-label">{{ monthLabel }}</span>
              <button class="nav-arrow" :disabled="isCurrentMonth" @click="nextMonth" title="Mois suivant">›</button>
            </div>

            <div v-if="loadingDomain" class="state-block">Chargement…</div>

            <template v-else-if="domainActivity">
              <!-- Dernières recherches (indépendant du mois) -->
              <div class="panel">
                <h3 class="panel-title">Dernières recherches</h3>
                <div v-if="!recent.length" class="state-block">Aucune interaction récente.</div>
                <template v-else>
                  <table class="usage-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Utilisateur</th>
                        <th>Mode</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(it, i) in visibleRecent" :key="i">
                        <td class="muted">{{ formatDateTime(it.created_at) }}</td>
                        <td class="user-email">{{ it.email }}</td>
                        <td><span class="mode-badge" :class="'mode-' + it.mode">{{ modeLabel(it.mode) }}</span></td>
                        <td>
                          <span class="status-dot" :class="'st-' + statusKind(it.status)"></span>
                          {{ statusLabel(it.status) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="feed-footer">
                    <span class="feed-count">{{ visibleRecent.length }} sur {{ recent.length }}</span>
                    <div class="feed-steps">
                      <span class="feed-steps-label">Afficher</span>
                      <button
                        v-for="n in recentSteps"
                        :key="n"
                        class="step-chip"
                        :class="{ active: recentLimit === n }"
                        @click="recentLimit = n"
                      >{{ n }}</button>
                    </div>
                  </div>
                </template>
              </div>

              <!-- KPIs -->
              <div class="kpi-row">
                <div class="kpi-card">
                  <div class="kpi-value">{{ domainActivity.totals.prompts }}</div>
                  <div class="kpi-label">Recherches</div>
                </div>
                <div class="kpi-card">
                  <div class="kpi-value">{{ domainActivity.totals.active_users }}<span class="kpi-sub">/ {{ domainActivity.members }}</span></div>
                  <div class="kpi-label">Utilisateurs actifs</div>
                </div>
                <div class="kpi-card">
                  <div class="kpi-value">{{ domainActivity.totals.avg_per_user }}</div>
                  <div class="kpi-label">Recherches / utilisateur actif</div>
                </div>
                <div class="kpi-card">
                  <div class="kpi-value">{{ domainActivity.totals.success_rate === null ? '—' : domainActivity.totals.success_rate + '%' }}</div>
                  <div class="kpi-label">Taux de succès</div>
                </div>
              </div>

              <!-- Graphe quotidien -->
              <div class="panel">
                <h3 class="panel-title">Recherches par jour</h3>
                <div v-if="domainActivity.totals.prompts === 0" class="state-block">Aucune activité ce mois-ci.</div>
                <div v-else class="bars">
                  <div
                    v-for="d in domainActivity.days"
                    :key="d.date"
                    class="bar-col"
                    :title="`${formatDay(d.date)} — ${d.count} recherche${d.count > 1 ? 's' : ''}`"
                  >
                    <span v-if="d.count" class="bar-count">{{ d.count }}</span>
                    <div class="bar" :style="{ height: barHeight(d.count) }"></div>
                    <span class="bar-day">{{ d.date.slice(8, 10) }}</span>
                  </div>
                </div>
              </div>

              <!-- Détail par utilisateur -->
              <div class="panel">
                <h3 class="panel-title">Détail par utilisateur</h3>
                <table class="usage-table">
                  <thead>
                    <tr>
                      <th>Utilisateur</th>
                      <th class="num">Recherches</th>
                      <th>Dernière activité</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!domainActivity.users.length"><td colspan="3" class="state-row">Aucun utilisateur actif ce mois-ci.</td></tr>
                    <tr v-for="u in domainActivity.users" :key="u.user_id">
                      <td class="user-email">{{ u.email }}</td>
                      <td class="num">{{ u.prompts }}</td>
                      <td class="muted">{{ formatDay(u.last_activity) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="content-grid">
        <div class="activity-section">
          <div class="section-header">
            <h2 class="section-title">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" class="bi bi-person-check" viewBox="0 0 16 16">
                <path d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m1.679-4.493-1.335 2.226a.75.75 0 0 1-1.174.144l-.774-.773a.5.5 0 0 1 .708-.708l.547.548 1.17-1.951a.5.5 0 1 1 .858.514M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0M8 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4"/>
                <path d="M8.256 14a4.5 4.5 0 0 1-.229-1.004H3c.001-.246.154-.986.832-1.664C4.484 10.68 5.711 10 8 10c.26 0 .507.009.74.025.226-.341.496-.65.804-.918C9.077 9.038 8.564 9 8 9c-5 0-6 3-6 4s1 1 1 1z"/>
              </svg>
              Activité (Essais)
            </h2>
            <div class="stats-summary" v-if="trialActivity">
              <div class="stat-item">
                <span class="stat-number">{{ trialActivity.total_requests || 0 }}</span>
                <span class="stat-label">requêtes</span>
              </div>
            </div>
          </div>
          <div v-if="loadingTrialActivity" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Chargement de l'activité...</p>
          </div>
          <div v-else-if="trialActivity && trialActivity.requests.length > 0" class="activity-content">
            <div class="daily-breakdown" v-if="trialActivity.daily_breakdown">
              <h4 class="breakdown-title">Répartition quotidienne</h4>
              <div class="breakdown-bars">
                <div v-for="(count, date) in trialActivity.daily_breakdown" :key="date" class="breakdown-bar">
                  <div class="bar-container">
                    <div class="bar-fill" :style="{ height: `${(count / maxTrialDailyCount) * 100}%` }"></div>
                  </div>
                  <div class="bar-label">
                    <div class="bar-date">{{ formatDate(date) }}</div>
                    <div class="bar-count">{{ count }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="requests-list">
              <h4 class="list-title">Dernières requêtes</h4>
              <div class="request-items">
                <InteractionCard
                  v-for="card in flattenToCards(trialActivity.requests.slice(0, 100))"
                  :key="card.key"
                  :name="card.name" :email="card.email"
                  :interaction="card.interaction"
                  :legacy-mode="card.legacyMode" :legacy-time="card.legacyTime"
                  :legacy-analysing="card.legacyAnalysing" />
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
              <path d="M2.5 3A1.5 1.5 0 0 0 1 4.5v.793c.026.009.051.02.076.032L7.674 8.51c.206.1.446.1.652 0l6.598-3.185A.755.755 0 0 1 15 5.293V4.5A1.5 1.5 0 0 0 13.5 3z"/>
              <path d="M15 6.954 8.978 9.86a2.25 2.25 0 0 1-1.956 0L1 6.954V11.5A1.5 1.5 0 0 0 2.5 13h11a1.5 1.5 0 0 0 1.5-1.5z"/>
            </svg>
            <p>Aucune activité pour les utilisateurs en essai.</p>
          </div>
        </div>

        <div>
          <div class="user-section" v-if="userSearchResults || searchingUser">
            <div class="section-header">
              <h2 class="section-title">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10s-3.516.68-4.168 1.332c-.678.678-.83 1.418-.832 1.664z"/>
                </svg>
                Dossiers de
                <span v-if="userSearchResults" class="user-email-badge">{{ userSearchResults.user_email }}</span>
              </h2>
              <div class="stats-summary" v-if="userSearchResults">
                <div class="stat-item">
                  <span class="stat-number">{{ userSearchResults.total_dossiers || 0 }}</span>
                  <span class="stat-label">dossiers</span>
                </div>
              </div>
            </div>
            <div v-if="searchingUser" class="loading-state">
              <div class="loading-spinner"></div>
              <p>Recherche des dossiers utilisateur...</p>
            </div>
            <div v-else-if="userSearchResults && userSearchResults.dossiers.length > 0" class="user-content">
              <div class="dossiers-list">
                <InteractionCard
                  v-for="card in flattenToCards(userSearchResults.dossiers)"
                  :key="card.key"
                  :name="card.name" :email="card.email"
                  :interaction="card.interaction"
                  :legacy-mode="card.legacyMode" :legacy-time="card.legacyTime"
                  :legacy-analysing="card.legacyAnalysing" />
              </div>
            </div>
            <div v-else-if="!searchingUser && userSearchResults" class="empty-state">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
                <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0"/>
                <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7"/>
              </svg>
              <p>Aucun dossier trouvé pour cet utilisateur.</p>
            </div>
          </div>

          <div class="activity-section" v-else>
            <div class="section-header">
              <h2 class="section-title">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71z"/>
                  <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16m7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0"/>
                </svg>
                Activité (Tous)
              </h2>
              <div class="stats-summary" v-if="recentActivity">
                <div class="stat-item">
                  <span class="stat-number">{{ recentActivity.total_requests || 0 }}</span>
                  <span class="stat-label">requêtes</span>
                </div>
              </div>
            </div>
            <div v-if="loadingRecent" class="loading-state">
              <div class="loading-spinner"></div>
              <p>Chargement de l'activité récente...</p>
            </div>
            <div v-else-if="recentActivity && recentActivity.requests.length > 0" class="activity-content">
              <div class="daily-breakdown" v-if="recentActivity.daily_breakdown">
                <h4 class="breakdown-title">Répartition quotidienne</h4>
                <div class="breakdown-bars">
                  <div v-for="(count, date) in recentActivity.daily_breakdown" :key="date" class="breakdown-bar">
                    <div class="bar-container">
                      <div class="bar-fill" :style="{ height: `${(count / maxDailyCount) * 100}%` }"></div>
                    </div>
                    <div class="bar-label">
                      <div class="bar-date">{{ formatDate(date) }}</div>
                      <div class="bar-count">{{ count }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="requests-list">
                <h4 class="list-title">Dernières requêtes</h4>
                <div class="request-items">
                  <InteractionCard
                    v-for="card in flattenToCards(recentActivity.requests.slice(0, 100))"
                    :key="card.key"
                    :name="card.name" :email="card.email"
                    :interaction="card.interaction"
                    :legacy-mode="card.legacyMode" :legacy-time="card.legacyTime"
                    :legacy-analysing="card.legacyAnalysing" />
                </div>
              </div>
            </div>
            <div v-else-if="!loadingRecent" class="empty-state">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
                 <path d="M2.5 3A1.5 1.5 0 0 0 1 4.5v.793c.026.009.051.02.076.032L7.674 8.51c.206.1.446.1.652 0l6.598-3.185A.755.755 0 0 1 15 5.293V4.5A1.5 1.5 0 0 0 13.5 3z"/>
                 <path d="M15 6.954 8.978 9.86a2.25 2.25 0 0 1-1.956 0L1 6.954V11.5A1.5 1.5 0 0 0 2.5 13h11a1.5 1.5 0 0 0 1.5-1.5z"/>
              </svg>
              <p>Aucune activité récente trouvée.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from "@/supabase/supabaseClient";
import InteractionCard from "@/components/InteractionCard.vue";

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function currentYM() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export default {
  name: 'ActivityPage',
  components: { InteractionCard },
  data() {
    return {
      loadingRecent: true,
      loadingTrialActivity: true,
      searchingUser: false,
      searchingDomain: false,
      recentActivity: null,
      trialActivity: null,
      userSearchResults: null,
      domainActivity: null,
      domainName: '',
      domainMonth: currentYM(),
      loadingDomain: false,
      recentLimit: 5,
      recentSteps: [5, 10, 20, 50],
      searchEmail: '',
      showDomainModal: false,
      adminPassword: null,
    }
  },
  computed: {
    monthLabel() {
      const [y, m] = this.domainMonth.split('-');
      return `${MOIS[parseInt(m, 10) - 1]} ${y}`;
    },
    isCurrentMonth() {
      return this.domainMonth >= currentYM();
    },
    maxDayCount() {
      if (!this.domainActivity || !this.domainActivity.days) return 1;
      return Math.max(...this.domainActivity.days.map(d => d.count), 1);
    },
    recent() {
      return (this.domainActivity && this.domainActivity.recent) || [];
    },
    visibleRecent() {
      return this.recent.slice(0, this.recentLimit);
    },
    maxDailyCount() {
      if (!this.recentActivity?.daily_breakdown) return 1;
      const max = Math.max(...Object.values(this.recentActivity.daily_breakdown));
      return max > 0 ? max : 1;
    },
    maxTrialDailyCount() {
      if (!this.trialActivity?.daily_breakdown) return 1;
      const max = Math.max(...Object.values(this.trialActivity.daily_breakdown));
      return max > 0 ? max : 1;
    }
  },
  methods: {
    // Aplatit une liste de dossiers/requests en cartes : 1 carte PAR interaction
    // (turn 0 + chaque follow-up). Les dossiers sans interaction analytics (legacy,
    // avant la télémétrie) → 1 carte fallback (mode/date/statut du dossier).
    flattenToCards(list) {
      if (!Array.isArray(list)) return [];
      const cards = [];
      for (const item of list) {
        const inters = Array.isArray(item.interactions) ? item.interactions : [];
        if (inters.length) {
          for (const it of inters) {
            cards.push({
              key: it.id,
              name: item.name,
              email: item.user_email || '',
              interaction: it,
            });
          }
        } else {
          cards.push({
            key: item.dossier_id,
            name: item.name,
            email: item.user_email || '',
            interaction: null,
            legacyMode: item.mode,
            legacyTime: item.created_at,
            legacyAnalysing: item.is_analysing,
          });
        }
      }
      return cards;
    },
    isDomain(input) {
      const trimmed = input.trim().toLowerCase();
      // Check if it's a domain: contains a dot, no @ symbol, and has at least 2 parts
      return trimmed.includes('.') && !trimmed.includes('@') && trimmed.split('.').length >= 2;
    },

    extractDomain(input) {
      // Remove @ if present and trim
      return input.trim().toLowerCase().replace(/^@/, '');
    },

    async searchUserOrDomain() {
      if (!this.searchEmail.trim()) return;

      if (this.isDomain(this.searchEmail)) {
        await this.searchDomainActivity();
      } else {
        await this.searchUserDossiers();
      }
    },

    async searchDomainActivity() {
      this.domainName = this.extractDomain(this.searchEmail);
      this.domainMonth = currentYM();
      this.domainActivity = null;
      this.recentLimit = 5;
      this.searchingDomain = true;
      try {
        await this.loadDomainActivity();
        this.showDomainModal = true;
      } finally {
        this.searchingDomain = false;
      }
    },

    async loadDomainActivity() {
      this.loadingDomain = true;
      try {
        const { data } = await supabase.auth.getSession();
        const jwt = data?.session?.access_token;
        if (!jwt) throw new Error('Session expirée');

        const adminPassword = this.promptForPassword();

        const response = await fetch('https://api.query-juriste.com/copilot/admin/get-domain-activity', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            domain: this.domainName,
            month: this.domainMonth,
            admin_password: adminPassword
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          if (response.status === 403) this.adminPassword = null;
          throw new Error(errorData.error || 'Erreur lors de la recherche du domaine');
        }

        this.domainActivity = await response.json();
      } catch (error) {
        console.error('Error searching domain:', error);
        this.$toast.error(error.message || 'Erreur de recherche du domaine');
      } finally {
        this.loadingDomain = false;
      }
    },

    shiftMonth(delta) {
      const [y, m] = this.domainMonth.split('-').map(Number);
      const d = new Date(y, m - 1 + delta, 1);
      this.domainMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      this.loadDomainActivity();
    },
    prevMonth() { this.shiftMonth(-1); },
    nextMonth() { if (!this.isCurrentMonth) this.shiftMonth(1); },

    barHeight(count) {
      if (!count) return '2px';
      return Math.max(6, Math.round(100 * count / this.maxDayCount)) + 'px';
    },

    formatDay(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (_) {
        return iso.slice(0, 10);
      }
    },

    modeLabel(mode) {
      return {
        flash: 'Réponse rapide',
        deep: 'Réponse longue',
        agent: 'Agent',
        tableau: 'Tableau'
      }[mode] || mode || '—';
    },

    statusKind(status) {
      if (status === 'done') return 'done';
      if (status === 'failed' || status === 'error') return 'failed';
      return 'pending';
    },

    statusLabel(status) {
      return { done: 'Réussi', failed: 'Échoué', pending: 'En cours' }[this.statusKind(status)];
    },

    promptForPassword() {
      if (!this.adminPassword) {
        const password = prompt('Mot de passe administrateur requis:');
        if (!password) {
          throw new Error('Mot de passe requis pour accéder aux données administrateur');
        }
        this.adminPassword = password;
      }
      return this.adminPassword;
    },

    async fetchTrialActivity() {
      this.loadingTrialActivity = true;
      try {
        const { data } = await supabase.auth.getSession();
        const jwt = data?.session?.access_token;
        if (!jwt) throw new Error('Session expirée');

        const adminPassword = this.promptForPassword();

        const response = await fetch('https://api.query-juriste.com/copilot/admin/get-trial-monitoring-request', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json',
            'X-Admin-Password': adminPassword
          }
        });

        if (!response.ok) {
          if (response.status === 403) this.adminPassword = null;
          throw new Error('Erreur lors du chargement de l\'activité des essais');
        }

        const result = await response.json();
        this.trialActivity = result;
      } catch (error) {
        console.error('Error fetching trial activity:', error);
        this.$toast.error(error.message || 'Erreur lors du chargement de l\'activité');
      } finally {
        this.loadingTrialActivity = false;
      }
    },

    async fetchRecentActivity() {
      this.loadingRecent = true
      try {
        const { data } = await supabase.auth.getSession()
        const jwt = data?.session?.access_token
        if (!jwt) throw new Error('Session expirée')

        const adminPassword = this.promptForPassword();

        const response = await fetch('https://api.query-juriste.com/copilot/admin/get-last-7-days-requests', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json',
            'X-Admin-Password': adminPassword
          }
        })

        if (!response.ok) {
          if (response.status === 403) this.adminPassword = null;
          throw new Error('Erreur de récupération des données')
        }

        const result = await response.json()
        this.recentActivity = result
      } catch (error) {
        console.error('Error fetching recent activity:', error)
        this.$toast.error(error.message || 'Erreur de chargement de l\'activité')
      } finally {
        this.loadingRecent = false
      }
    },

    async searchUserDossiers() {
      if (!this.searchEmail.trim()) return

      this.searchingUser = true
      this.userSearchResults = null

      try {
        const { data } = await supabase.auth.getSession()
        const jwt = data?.session?.access_token
        if (!jwt) throw new Error('Session expirée')

        const adminPassword = this.promptForPassword();

        const response = await fetch('https://api.query-juriste.com/copilot/admin/get-user-dossiers', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: this.searchEmail.trim(),
            admin_password: adminPassword
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          if (response.status === 403) this.adminPassword = null;
          throw new Error(errorData.error || 'Erreur lors de la recherche')
        }

        const result = await response.json()
        this.userSearchResults = result
      } catch (error) {
        console.error('Error searching user dossiers:', error)
        this.$toast.error(error.message || 'Erreur de recherche utilisateur')
      } finally {
        this.searchingUser = false
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        month: 'short',
        day: 'numeric'
      })
    },

    formatDateTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('fr-FR', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getStatusClass(status) {
      switch (status) {
        case 'done': return 'status-done';
        case 'pending': return 'status-pending';
        default: return 'status-unknown';
      }
    },

    getStatusText(status) {
      switch (status) {
        case 'done': return 'Terminé';
        case 'pending': return 'En cours';
        default: return 'Inconnu';
      }
    }
  },

  async mounted() {
    await this.fetchRecentActivity();
    await this.fetchTrialActivity();

    // Auto-search if ?search=... query param present (used by CoSell brief links)
    const q = this.$route?.query?.search;
    if (q) {
      this.searchEmail = String(q);
      await this.searchUserOrDomain();
    }
  }
}
</script>

<style scoped>
.activity-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem 1rem;
}

.activity-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.activity-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  color: white;
}

.header-content {
  flex: 1;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.page-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
  font-weight: 300;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

/* Search Section */
.search-section {
  margin-bottom: 2rem;
}

.search-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.search-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
}

.search-input-group {
  display: flex;
  gap: 1rem;
  max-width: 700px;
}

.search-input {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
}

.search-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 12px 20px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.search-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.search-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.search-hint {
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #4a5568;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 2px solid white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(4px);
}

.modal-container {
  background: white;
  border-radius: 16px;
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.modal-header {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
}

.modal-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
}

.modal-close {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

.modal-content {
  padding: 2rem;
  overflow-y: auto;
  flex: 1;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  padding: 1.5rem;
  border-radius: 12px;
  border: 2px solid;
}

.stat-card-blue {
  background: linear-gradient(135deg, #ebf4ff 0%, #c3dafe 100%);
  border-color: #90cdf4;
}

.stat-card-green {
  background: linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%);
  border-color: #9ae6b4;
}

.stat-card-purple {
  background: linear-gradient(135deg, #faf5ff 0%, #e9d8fd 100%);
  border-color: #d6bcfa;
}

.stat-card-orange {
  background: linear-gradient(135deg, #fffaf0 0%, #feebc8 100%);
  border-color: #fbd38d;
}

.stat-label {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
  opacity: 0.8;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
}

.stat-percentage {
  font-size: 0.9rem;
  margin-left: 0.5rem;
  opacity: 0.7;
}

/* Section Container */
.section-container {
  margin-bottom: 2rem;
}

.section-heading {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2d3748;
}

/* Table Styles */
.table-container {
  background: #f7fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  max-height: 300px;
  overflow-y: auto;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
}

.users-table thead {
  position: sticky;
  top: 0;
  background: #edf2f7;
  z-index: 1;
}

.users-table th {
  padding: 1rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  border-bottom: 2px solid #e2e8f0;
}

.th-count {
  text-align: right;
}

.users-table td {
  padding: 1rem;
  font-size: 0.875rem;
}

.td-email {
  color: #2d3748;
}

.td-date {
  color: #718096;
}

.td-count {
  text-align: right;
}

.row-even {
  background: white;
}

.row-odd {
  background: #f7fafc;
}

.count-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.75rem;
}

.count-active {
  background: #c6f6d5;
  color: #22543d;
}

.count-inactive {
  background: #edf2f7;
  color: #718096;
}

/* Requests Container */
.requests-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.request-card {
  background: #f7fafc;
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.request-card:hover {
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.request-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.request-email {
  font-weight: 600;
  color: #2d3748;
}

.request-time {
  font-size: 0.8rem;
  color: #718096;
}

.request-title {
  margin: 0 0 0.5rem 0;
  font-weight: 500;
  color: #4a5568;
  font-size: 0.9rem;
}

.request-excerpt {
  margin: 0;
  font-size: 0.85rem;
  color: #718096;
  line-height: 1.4;
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: start;
}

@media (min-width: 1200px) {
  .content-grid {
    grid-template-columns: 1fr 1fr;
  }
}

/* Sections */
.activity-section,
.user-section {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f7fafc;
  flex-shrink: 0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
}

.user-email-badge {
  padding: 4px 12px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-left: 1rem;
}

.stats-summary {
  display: flex;
  gap: 1rem;
}

.stat-item {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #667eea;
}

.stat-label {
  display: block;
  font-size: 0.8rem;
  color: #718096;
  font-weight: 500;
}

/* Loading and Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: #718096;
  flex-grow: 1;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(102, 126, 234, 0.2);
  border-radius: 50%;
  border-top: 3px solid #667eea;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.empty-state svg {
  opacity: 0.5;
  margin-bottom: 1rem;
}

/* Daily Breakdown */
.daily-breakdown {
  margin-bottom: 2rem;
}

.breakdown-title {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #4a5568;
}

.breakdown-bars {
  display: flex;
  align-items: end;
  gap: 0.5rem;
  height: 120px;
  padding: 1rem 0;
}

.breakdown-bar {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.bar-container {
  flex: 1;
  width: 100%;
  background: #f7fafc;
  border-radius: 4px;
  position: relative;
  display: flex;
  align-items: end;
}

.bar-fill {
  width: 100%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 4px;
  min-height: 4px;
  transition: height 0.3s ease;
}

.bar-label {
  text-align: center;
}

.bar-date {
  font-size: 0.7rem;
  color: #718096;
  font-weight: 500;
}

.bar-count {
  font-size: 0.8rem;
  color: #4a5568;
  font-weight: 600;
}

/* Request/Dossier Lists */
.requests-list,
.dossiers-list {
  margin-top: 1rem;
}

.list-title {
  margin: 1.5rem 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #4a5568;
}
.list-title:first-child {
  margin-top: 0;
}

.request-items,
.dossiers-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.request-item,
.dossier-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.request-item:hover,
.dossier-item:hover {
  border-color: #667eea;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.request-info,
.dossier-info {
  flex: 1;
  overflow: hidden;
}

.request-header,
.dossier-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.request-user,
.dossier-name {
  font-weight: 600;
  color: #2d3748;
}

.request-time,
.dossier-time {
  font-size: 0.8rem;
  color: #718096;
}

.request-details,
.dossier-details {
  overflow: hidden;
}

.request-name {
  margin: 0 0 0.25rem 0;
  font-weight: 500;
  color: #4a5568;
  font-size: 0.9rem;
}

.request-excerpt,
.dossier-excerpt {
  margin: 0;
  font-size: 0.85rem;
  color: #718096;
  line-height: 1.4;
}

.request-status,
.dossier-status {
  flex-shrink: 0;
  margin-left: 1rem;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-done {
  background: #c6f6d5;
  color: #22543d;
}

.status-pending {
  background: #fef5e7;
  color: #c05621;
}

.status-unknown {
  background: #edf2f7;
  color: #4a5568;
}

/* Responsive Design */
@media (max-width: 768px) {
  .activity-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .page-title {
    font-size: 2rem;
  }

  .search-input-group {
    flex-direction: column;
  }

  .content-grid {
    grid-template-columns: 1fr;
  }

  .breakdown-bars {
    gap: 0.25rem;
  }

  .request-header,
  .dossier-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .section-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .modal-container {
    max-height: 95vh;
  }

  .modal-content {
    padding: 1rem;
  }
}

/* Add this to your <style scoped> section */

.flash-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%); /* Gold Gradient */
  color: #854d0e; /* Dark Gold/Brown text */
  font-size: 0.65rem;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 5px rgba(234, 179, 8, 0.3);
  vertical-align: middle;
  border: 1px solid rgba(255, 255, 255, 0.4);
}

.agent-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
  color: #6d28d9;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 5px rgba(109, 40, 217, 0.2);
  vertical-align: middle;
  border: 1px solid rgba(124, 58, 237, 0.3);
}

/* ===== Domain modal — vue calquée sur CabAdminUsage ===== */
.month-nav {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.month-label {
  min-width: 140px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}
.nav-arrow {
  padding: 6px 14px;
  font-size: 16px;
  line-height: 1;
  border-radius: 6px;
  border: 1px solid #ddd;
  background: white;
  color: #555;
  cursor: pointer;
  font-weight: 500;
}
.nav-arrow:hover:not(:disabled) { border-color: #999; color: #222; }
.nav-arrow:disabled { opacity: 0.4; cursor: not-allowed; }

.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
.kpi-card {
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}
.kpi-value { font-size: 26px; font-weight: 700; color: #6c63ff; }
.kpi-sub { font-size: 14px; font-weight: 500; color: #999; margin-left: 4px; }
.kpi-label { margin-top: 4px; font-size: 12px; color: #777; }

.panel {
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}
.panel-title {
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bars {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 140px;
  padding-bottom: 18px;
  position: relative;
}
.bar-col {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  position: relative;
  min-width: 0;
}
.bar {
  width: 100%;
  max-width: 26px;
  background: #6c63ff;
  border-radius: 3px 3px 0 0;
}
.bar-count { font-size: 10px; color: #555; margin-bottom: 2px; }
.bar-day {
  position: absolute;
  bottom: -18px;
  font-size: 9px;
  color: #999;
}

.usage-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.usage-table th, .usage-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
}
.usage-table th { background: #fafafa; font-weight: 600; color: #555; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; }
.usage-table tbody tr:hover { background: #fafbff; }
.usage-table tbody tr:last-child td { border-bottom: none; }
.usage-table .num { text-align: right; font-variant-numeric: tabular-nums; }
.user-email { font-weight: 500; }
.muted { color: #777; }
.state-row { text-align: center; padding: 24px 16px; color: #999; }
.state-block { padding: 32px 16px; text-align: center; color: #777; }

.mode-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.78em;
  font-weight: 600;
  white-space: nowrap;
  background: #eef2ff;
  color: #4f46e5;
}
.mode-badge.mode-agent { background: #ede9fe; color: #6c63ff; }
.mode-badge.mode-deep { background: #fef3c7; color: #b45309; }
.mode-badge.mode-flash { background: #dbeafe; color: #1d4ed8; }
.status-dot {
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
  background: #9ca3af;
}
.status-dot.st-done { background: #16a34a; }
.status-dot.st-failed { background: #dc2626; }
.status-dot.st-pending { background: #f59e0b; }

.feed-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.feed-count { font-size: 12px; color: #999; }
.feed-steps { display: flex; align-items: center; gap: 4px; }
.feed-steps-label { font-size: 12px; color: #999; margin-right: 4px; }
.step-chip {
  border: 1px solid #ddd;
  background: white;
  color: #555;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}
.step-chip:hover { border-color: #999; }
.step-chip.active { background: #6c63ff; color: white; border-color: #6c63ff; }
</style>
